import React, { useCallback, useMemo } from 'react'
import { toast } from 'react-hot-toast/headless'
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary'
import { SomethingWentWrong } from '@components/Errors/SomethingWentWrong'
import { StandardToast } from '@components/Notifications/StandardToast'
import { TimelineEvent } from '@towns-protocol/sdk'
import { Box } from '@ui'
import { useHandleReaction } from 'hooks/useReactions'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import {
    useMyProfile,
    usePins,
    useSpaceMembers,
    useTimelineThreadStats,
    useTownsClient,
} from 'use-towns-client'

import { useTimelineMessageEditing } from './hooks/useTimelineMessageEditing'
import { MessageTimelineContext, MessageTimelineType } from './MessageTimelineContext'

export const MessageTimelineWrapper = (props: {
    children: React.ReactNode
    spaceId: string | undefined
    channelId: string
    events: TimelineEvent[]
    threadParentId?: string
    isChannelWritable?: boolean
    isChannelPinnable?: boolean
    isChannelReactable?: boolean
}) => {
    const {
        spaceId,
        channelId,
        events,
        isChannelWritable,
        isChannelPinnable,
        threadParentId,
        isChannelReactable,
    } = props

    const userId = useMyProfile()?.userId
    const { sendReadReceipt } = useTownsClient()
    const type = threadParentId ? MessageTimelineType.Thread : MessageTimelineType.Channel
    const channels = useSpaceChannels()
    const messageRepliesMap = useTimelineThreadStats(channelId)
    const timelineActions = useTimelineMessageEditing()
    const handleReaction = useHandleReaction(channelId)
    const isChannelEncrypted = true

    const { memberIds } = useSpaceMembers()

    const { openPanel } = usePanelActions()

    const pins = usePins(channelId)

    const onMentionClick = useCallback(
        async (userId: string) => {
            if (!userId) {
                return
            }
            try {
                openPanel('profile', { profileId: userId })
            } catch (error) {
                console.error('Error navigating to user profile', error)
                toast.custom((t) => {
                    return (
                        <StandardToast.Error
                            toast={t}
                            message="There was an error navigating to this users's profile."
                        />
                    )
                })
            }
        },
        [openPanel],
    )

    const value = useMemo(() => {
        return {
            channelId,
            channels,
            events,
            handleReaction: isChannelReactable ? handleReaction : undefined,
            isChannelEncrypted,
            isChannelWritable,
            isChannelPinnable,
            isChannelReactable,
            messageRepliesMap,
            onMentionClick,
            memberIds,
            pins,
            sendReadReceipt,
            spaceId,
            threadParentId,
            timelineActions,
            type,
            userId,
        }
    }, [
        channelId,
        channels,
        events,
        handleReaction,
        isChannelEncrypted,
        isChannelWritable,
        isChannelPinnable,
        isChannelReactable,
        messageRepliesMap,
        onMentionClick,
        memberIds,
        pins,
        sendReadReceipt,
        spaceId,
        threadParentId,
        timelineActions,
        type,
        userId,
    ])

    return (
        <ErrorBoundary FallbackComponent={MessageTimelineFallbackComponent}>
            <MessageTimelineContext.Provider value={value}>
                {props.children}
            </MessageTimelineContext.Provider>
        </ErrorBoundary>
    )
}

const MessageTimelineFallbackComponent = (props: { error: Error }) => {
    return (
        <Box centerContent height="100%">
            <SomethingWentWrong error={props.error} />
        </Box>
    )
}
